import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { API_URL, fmtUGX } from './feesUtils';

const EMPTY = { class_name: '', school_fees: '', registration_fee: '', meals: '', other_charges: '', notes: '' };

export const FeeStructureTab = ({ config, school, year, term, refresh, refreshKey }) => {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const classes = config?.schools?.find((s) => s.key === school)?.classes || [];

  const load = useCallback(() => {
    axios.get(`${API_URL}/api/fees/structures`, { params: { school, academic_year: year, term } })
      .then((r) => setItems(r.data.items || [])).catch(() => {});
  }, [school, year, term]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const openEdit = (s) => {
    setEditing(s);
    setForm({ class_name: s.class_name, school_fees: String(s.school_fees), registration_fee: String(s.registration_fee), meals: String(s.meals), other_charges: String(s.other_charges), notes: s.notes || '' });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        school, class_name: form.class_name, academic_year: year, term,
        school_fees: parseFloat(form.school_fees || 0),
        registration_fee: parseFloat(form.registration_fee || 0),
        meals: parseFloat(form.meals || 0),
        other_charges: parseFloat(form.other_charges || 0),
        notes: form.notes || null,
      };
      if (editing) {
        await axios.put(`${API_URL}/api/fees/structures/${editing.id}`, payload);
        toast.success('Fee structure updated');
      } else {
        await axios.post(`${API_URL}/api/fees/structures`, payload);
        toast.success('Fee structure saved');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save fee structure');
    }
  };

  const configured = new Set(items.map((i) => i.class_name));
  return (
    <Card>
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="font-heading text-base">Fee Structure — {year} Term {term}</CardTitle>
          <CardDescription>Expected fees per class · historical structures are preserved per year/term</CardDescription>
        </div>
        <Button size="sm" onClick={() => { setEditing(null); setForm(EMPTY); setDialogOpen(true); }} data-testid="add-structure-btn">
          <Plus className="h-4 w-4 mr-1" /> Set Class Fees
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Class</TableHead><TableHead className="text-right">School Fees</TableHead>
            <TableHead className="text-right">Registration</TableHead><TableHead className="text-right">Meals</TableHead>
            <TableHead className="text-right">Other</TableHead><TableHead className="text-right">Total Expected</TableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {classes.map((c) => {
              const s = items.find((i) => i.class_name === c);
              return (
                <TableRow key={c} className={!s ? 'opacity-60' : ''} data-testid={`structure-row-${c.replace(/ /g, '-')}`}>
                  <TableCell className="font-medium">{c}</TableCell>
                  <TableCell className="text-right">{s ? fmtUGX(s.school_fees) : '—'}</TableCell>
                  <TableCell className="text-right">{s ? fmtUGX(s.registration_fee) : '—'}</TableCell>
                  <TableCell className="text-right">{s ? fmtUGX(s.meals) : '—'}</TableCell>
                  <TableCell className="text-right">{s ? fmtUGX(s.other_charges) : '—'}</TableCell>
                  <TableCell className="text-right font-semibold">{s ? fmtUGX(s.total_expected) : 'Not configured'}</TableCell>
                  <TableCell className="text-right">
                    {s && <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => openEdit(s)} data-testid={`edit-structure-${c.replace(/ /g, '-')}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? `Edit Fees — ${editing.class_name}` : 'Set Class Fees'}</DialogTitle>
            <DialogDescription>{year} · Term {term}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {!editing && (
              <div className="space-y-1.5"><Label>Class *</Label>
                <Select value={form.class_name} onValueChange={(v) => setForm({ ...form, class_name: v })}>
                  <SelectTrigger data-testid="structure-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
                  <SelectContent>{classes.filter((c) => !configured.has(c)).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
            )}
            <div className="grid grid-cols-2 gap-3">
              {[['school_fees', 'School Fees (UGX)'], ['registration_fee', 'Registration Fee'], ['meals', 'Meals'], ['other_charges', 'Other Charges']].map(([k, l]) => (
                <div key={k} className="space-y-1.5"><Label>{l}</Label>
                  <Input type="number" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`structure-${k}`} /></div>
              ))}
            </div>
            <div className="space-y-1.5"><Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={!editing && !form.class_name} data-testid="structure-submit">Save Fees</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
